<script lang="ts">
    
    import { createEventDispatcher, onMount } from "svelte";
    import { Button } from "sveltestrap";
    import type { FormMember } from "../Model/FormMember";
    import { instancesService } from "../Services";

    export let member: FormMember;

    const dispatch = createEventDispatcher();

    let canvasElement: HTMLCanvasElement;
    let ctx: CanvasRenderingContext2D;
    let isCaptured = false;
    let isSigning = false;

    onMount(() => {
        ctx = canvasElement.getContext('2d');

        const width = Math.min(canvasElement.parentElement.clientWidth, 375);
        const height = width * 0.65;

        canvasElement.width = width;
        canvasElement.height = height;

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
        clear();
    });

    function onTouchstart(event: TouchEvent | MouseEvent): void {
        if (isCaptured) {
            return;
        }
        isCaptured = true;
        const { left, top } = extractEventPosition(event);
        ctx.beginPath();
        ctx.arc(left, top, 2, 0, 360);
        ctx.fill();
    }

    function onTouchmove(event: TouchEvent | MouseEvent): void {
        if (!isCaptured) {
            return;
        }
        const { left, top } = extractEventPosition(event);
        ctx.lineTo(left, top);
        ctx.stroke();
    }

    function onTouchend(): void {
        isCaptured = false;
    }

    function extractEventPosition(event: TouchEvent | MouseEvent) {
        const { left, top } = canvasElement.getBoundingClientRect();
        if (isTouchEvent(event)) {
            const [touch] = event.touches;
            return {
                left: touch.clientX - left,
                top: touch.clientY - top,
            };
        }
        return {
            left: event.clientX - left,
            top: event.clientY - top,
        };
    }

    function isTouchEvent(event: TouchEvent | MouseEvent): event is TouchEvent {
        return 'touches' in event;
    }

    function clear(): void {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvasElement.width, canvasElement.height);
        ctx.fillStyle = "#000000";
    }

    async function onSave(): Promise<void> {
        if (isSigning) {
            return;
        }
        isSigning = true;
        try {
            const blob = await toBlob();
            await instancesService.signInstance(member.instance.id, member.userId, blob);
            member.isSigned = true;
            dispatch('signed');
        } catch (error) {
            console.error(error);
        } finally {
            isSigning = false;
        }
    }

    function toBlob(): Promise<Blob> {
        return new Promise(resolve => canvasElement.toBlob(resolve, 'image/png'));
    }

</script>

<svelte:window
    on:touchmove={onTouchmove}
    on:touchend={onTouchend}
    on:mousemove={onTouchmove}
    on:mouseup={onTouchend} />

<div class="sign-form">
    <canvas
        bind:this={canvasElement}
        on:touchstart|nonpassive|preventDefault={onTouchstart}
        on:mousedown|nonpassive|preventDefault={onTouchstart}></canvas>

    <div class="w-100 d-flex justify-content-between">
        <Button type="button" color="danger" on:click={clear}>
            Borrar
        </Button>
        <Button type="button" color="primary" on:click={onSave} disabled={isSigning}>
            {#if isSigning}
                Guadando...
            {:else}
                Guardar
            {/if}
        </Button>
    </div>
</div>

<style lang="scss">

    .sign-form {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
    }

    canvas {
        border: 1px solid #333;
    }

</style>
